/**
 * The app's addresses.
 *
 * Everything used to live at `/`. That is fine until you want to know which
 * screen people actually use: analytics tools group by URL, so seven sections
 * sharing one address collapse into a single unreadable heatmap. It is also
 * the reason the back button behaved oddly and why nobody could link a friend
 * to the story.
 *
 * The tab id and the path are deliberately allowed to differ. Tab ids are
 * internal and some of them are historical — the verse deck is still called
 * `flashcards` in the code — while paths are read by people and end up in
 * links, so they get the name the section is actually called in the interface.
 *
 * Anything not listed here resolves to home rather than to an error page. A
 * mistyped or stale URL should land somewhere useful, and this app has no
 * concept of a 404 to show.
 */

/** The one place a tab id and a public path are tied together. */
export const ROUTES = [
  { tab: 'home', path: '/' },
  { tab: 'story', path: '/story' },
  { tab: 'practice', path: '/practice' },
  { tab: 'flashcards', path: '/verses' },
  { tab: 'people', path: '/people' },
  { tab: 'saved', path: '/saved' },
  { tab: 'chat', path: '/ask' },
  { tab: 'paths', path: '/paths' },
];

/** The character quiz, which had its own address before any of this existed. */
export const QUIZ_PATH = '/quiz';

/**
 * Games inside Practice that are worth linking to directly.
 *
 * Anything promoted anywhere — a video, a post, a link in a bio — needs an
 * address that opens it, not an address that opens the app near it. Somebody
 * who has just watched ten seconds of a game will not hunt through a tab bar
 * for it, and every tap between the link and the thing loses people.
 *
 * Only the games that get promoted are listed. The rest are reached from the
 * Practice hub and do not need their own front door.
 */
export const PRACTICE_ROUTES = [
  { mode: 'beforeafter', path: '/before-or-after' },
  { mode: 'toscale', path: '/to-scale' },
  { mode: 'thirdday', path: '/third-day' },
  { mode: 'twentyfaces', path: '/twenty-faces' },
];

export const HOME_TAB = 'home';

/**
 * Normalise before matching: a trailing slash and a capital letter are the two
 * ways a perfectly valid link arrives looking like a different one.
 */
function normalise(pathname) {
  const trimmed = String(pathname ?? '/')
    .split('?')[0]
    .split('#')[0]
    .replace(/\/+$/, '')
    .toLowerCase();
  return trimmed === '' ? '/' : trimmed;
}

/** Path for a tab id. Unknown ids fall back to home rather than throwing. */
export function pathForTab(tab) {
  return ROUTES.find((route) => route.tab === tab)?.path ?? '/';
}

/** Tab id for a path, or null when the path is not one of ours. */
export function tabForPath(pathname) {
  const path = normalise(pathname);
  if (practiceModeForPath(path)) return 'practice';
  return ROUTES.find((route) => route.path === path)?.tab ?? null;
}

/** The practice game this path opens, or null. */
export function practiceModeForPath(pathname) {
  const path = normalise(pathname);
  return PRACTICE_ROUTES.find((route) => route.path === path)?.mode ?? null;
}

/** Whether this path is the quiz. */
export function isQuizPath(pathname) {
  return normalise(pathname) === QUIZ_PATH;
}

/**
 * What the address bar should say, given what is on screen.
 *
 * Most specific wins: the quiz sits on top of whichever tab is behind it, and
 * a named game inside Practice is more precise than Practice itself. A game
 * with no path of its own falls back to /practice, which is correct — it is
 * reachable from there, and inventing an address for it would promise a front
 * door that nothing links to.
 */
export function pathForState({ tab, quizOpen, practiceMode = null }) {
  if (quizOpen) return QUIZ_PATH;
  if (tab === 'practice' && practiceMode) {
    const named = PRACTICE_ROUTES.find((route) => route.mode === practiceMode);
    if (named) return named.path;
  }
  return pathForTab(tab);
}
